// A seller we did not write to that wrote about one of our RFQs (it got the RFQ through someone
// else), opened beside the list: its latest mail, and the buyer's answer. A new seller gets its own
// conversation, opened right after; the answer covers every mail of that seller on the RFQ.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/ReferralPanel.tsx

import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography, useTheme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import rfqCenterService, { CenterReferral, ReferralChoice } from '../../../services/rfqCenterService';

interface ReferralPanelProps {
  referral: CenterReferral;
  onClose: () => void;
  /** After the answer: the new seller's RFQ to open, when there is one. */
  onDone: (openRfqId?: string) => void;
}

const LABEL_SX = { fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, color: 'text.disabled' } as const;

const ReferralPanel: React.FC<ReferralPanelProps> = ({ referral, onClose, onDone }) => {
  const theme = useTheme();
  const [name, setName] = useState(referral.domain);
  const [busy, setBusy] = useState<ReferralChoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setName(referral.domain);
    setError(null);
  }, [referral.mailId, referral.domain]);

  const answer = async (choice: ReferralChoice) => {
    setBusy(choice);
    setError(null);
    try {
      const result = await rfqCenterService.resolveReferral(referral.mailId, { choice, rfqNumber: referral.rfqNumber, sellerName: choice === 'new-seller' ? name.trim() || referral.domain : undefined });
      onDone(result?.seller?.rfqId);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      setError(err?.response?.data?.message || err?.message || 'That did not go through.');
    } finally {
      setBusy(null);
    }
  };

  const cells = [
    { label: 'Our RFQ', value: referral.rfqNumber ?? '' },
    { label: 'Item', value: referral.item },
    { label: 'Sent to', value: referral.referrerName ?? '' },
    { label: 'Mails', value: String(referral.mails) },
  ];

  return (
    <Box sx={{ bgcolor: 'background.paper', border: `1px solid ${theme.palette.divider}`, borderRadius: 2, minWidth: 0 }}>
      <Stack direction="row" spacing={1} sx={{ px: 2.5, py: 1, alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="body2" fontWeight={700}>
          New seller?
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={onClose} startIcon={<CloseIcon fontSize="small" />} sx={{ color: 'text.secondary' }}>
          Close
        </Button>
      </Stack>

      <Box sx={{ px: 2.5, pt: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
          {referral.domain}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Got our RFQ through {referral.referrerName ?? 'another seller'}
        </Typography>
        {!!referral.automatic?.length && (
          <Typography variant="body2" sx={{ mt: 1, color: theme.palette.warning.dark }}>
            Automatic reminders: {referral.automatic.join(', ')}
          </Typography>
        )}
      </Box>

      <Box sx={{ mx: 2.5, mt: 1.25, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', lg: 'repeat(4, minmax(0, 1fr))' }, overflow: 'hidden' }}>
        {cells.map((c) => (
          <Box key={c.label} sx={{ px: 1.75, py: 1.25, minWidth: 0, borderRight: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`, mr: '-1px', mb: '-1px' }}>
            <Typography sx={LABEL_SX}>{c.label}</Typography>
            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
              {c.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box sx={{ px: 2.5, pt: 2.5 }}>
        <Typography sx={{ ...LABEL_SX, mb: 0.75 }}>Latest mail</Typography>
        <Box sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2, px: 2, py: 1.5 }}>
          <Typography variant="body2" fontWeight={700} sx={{ overflowWrap: 'anywhere' }}>
            {referral.latest.name ? `${referral.latest.name} <${referral.latest.email}>` : referral.latest.email}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, overflowWrap: 'anywhere' }}>
            {referral.latest.subject}
            {referral.lastAt ? ` · ${new Date(referral.lastAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}` : ''}
          </Typography>
          <Typography dir="auto" variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxHeight: 280, overflowY: 'auto' }}>
            {referral.latest.text}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2.5, pt: 2.5, pb: 2.5 }}>
        <TextField size="small" label="Seller name" value={name} onChange={(e) => setName(e.target.value)} fullWidth sx={{ maxWidth: 420 }} />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          <Button variant="contained" disableElevation disabled={!!busy || !name.trim()} onClick={() => void answer('new-seller')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            {busy === 'new-seller' ? 'Adding…' : 'Add as new seller'}
          </Button>
          <Button variant="outlined" disabled={!!busy} onClick={() => void answer('same-seller')} sx={{ textTransform: 'none' }}>
            {busy === 'same-seller' ? 'Saving…' : `Same as ${referral.referrerName ?? 'that seller'}`}
          </Button>
          <Button disabled={!!busy} onClick={() => void answer('not-ours')} sx={{ textTransform: 'none', color: 'text.secondary' }}>
            Not ours
          </Button>
        </Stack>
        {error && (
          <Alert severity="error" sx={{ mt: 1.5 }}>
            {error}
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ReferralPanel;
